import { useNavigate } from 'react-router-dom';
import {
    User,
    Bell,
    Heart,
    Gift,
    Settings,
    CircleHelp,
    ChevronRight,
    TrendingUp,
    Sparkles,
    MapPin,
    Award,
    ArrowRightLeft,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useRedemptions, useLevels, type Level } from '../hooks/useData';
import './Profile.css';

const Profile = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const toast = useToast();
    const { data: redemptions } = useRedemptions(user?.id);
    const { data: levels } = useLevels();

    const history = redemptions.filter((item) => !!item.usedAt);
    const totalVisits = user?.visitCount ?? history.length;

    const sortedLevels = [...(levels || [])].sort((a, b) => a.minVisits - b.minVisits);
    const currentLevel: Level | null = sortedLevels.filter((l) => totalVisits >= l.minVisits).pop() || null;
    const nextLevel: Level | null = sortedLevels.find((l) => l.minVisits > totalVisits) || null;

    const firstLevel = sortedLevels[0];
    const defaultLevel = {
        title: 'Nível Bronze',
        discount: '3% a 5% de desconto',
        color: '#ea580c',
        minVisits: 10,
        slug: 'bronze',
    } as Level;
    const levelConfig = currentLevel || firstLevel || defaultLevel;
    const targetVisits = nextLevel?.minVisits ?? currentLevel?.minVisits ?? 10;
    const progressStart = currentLevel ? currentLevel.minVisits : 0;
    const progressCurrent = Math.min(totalVisits, targetVisits);
    const range = targetVisits - progressStart;
    const progressPercent = range > 0
        ? Math.min(((progressCurrent - progressStart) / range) * 100, 100)
        : 100;

    const getSavedValue = (item: { discountSaved?: number; promotion?: { discount?: string } }) => {
        if (item.discountSaved != null && item.discountSaved > 0) return item.discountSaved;
        const discount = item.promotion?.discount || '';
        const moneyMatch = discount.replace(',', '.').match(/r\$\s*(\d+(\.\d+)?)/i);
        return moneyMatch ? Number(moneyMatch[1]) : 0;
    };

    const totalSaved = history.reduce((sum, item) => sum + getSavedValue(item), 0);

    const getNextLevelLabel = () => nextLevel?.title?.replace('Nível ', '') || 'Elite';

    const menuItems = [
        { key: 'account', label: 'Minha Conta', icon: User, iconClass: 'icon-blue', action: () => toast.info('Edicao de conta em breve!') },
        { key: 'notifications', label: 'Notificacoes', icon: Bell, iconClass: 'icon-purple', action: () => navigate('/notifications') },
        { key: 'favorites', label: 'Favoritos', icon: Heart, iconClass: 'icon-red', action: () => toast.info('Favoritos em breve!') },
        { key: 'benefits-history', label: 'Historico de Beneficios', icon: Gift, iconClass: 'icon-teal', action: () => navigate('/benefits') },
        { key: 'settings', label: 'Configuracoes', icon: Settings, iconClass: 'icon-gray', action: () => toast.info('Configuracoes em breve!') },
        { key: 'support', label: 'Ajuda & Suporte', icon: CircleHelp, iconClass: 'icon-orange', action: () => toast.info('Suporte em breve!') },
    ];

    const initials = (user?.name || 'TU')
        .split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="profile-page">
            <section className="profile-hero">
                <div className="avatar-circle">
                    {user?.avatar ? <img src={user.avatar} alt={user.name} /> : <span>{initials}</span>}
                </div>
                <h1>{user?.name || 'Turista Ubatuba'}</h1>
                <p>{user?.email || 'turista@email.com'}</p>
                <span
                    className="profile-tier-pill"
                    style={{
                        background: `linear-gradient(90deg, ${levelConfig.color}, ${levelConfig.color}dd)`,
                        color: '#fff',
                        boxShadow: `0 2px 8px ${levelConfig.color}40`,
                    }}
                >
                    <Award size={16} />
                    Cliente {levelConfig.title?.replace('Nível ', '') || 'Bronze'}
                </span>
            </section>

            <div className="profile-content">
                <section className="savings-card">
                    <div className="savings-icon">
                        <TrendingUp size={34} />
                    </div>
                    <div className="savings-copy">
                        <p>Economia com o Pass+</p>
                        <h2>
                            {totalSaved.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                            })}
                        </h2>
                        <span>Em descontos aplicados nas suas {history.length} visitas com cupom.</span>
                    </div>
                </section>

                <section className="referral-teaser-card" aria-label="Em breve: ganhos por indicação">
                    <div className="referral-teaser-icon">
                        <Gift size={26} />
                    </div>
                    <div className="referral-teaser-copy">
                        <span className="referral-teaser-pill">Em breve</span>
                        <h3>Ganhos por indicação</h3>
                        <p>Convide amigos para o Facilita Pass+ e receba uma parte das compras deles em parceiros do app.</p>
                    </div>
                </section>

                <section className="card level-card">
                    <div className="level-header">
                        <div className="level-info">
                            <div className="level-title-row">
                                <Sparkles size={18} />
                                <h3>{levelConfig.title}</h3>
                            </div>
                            <p>{levelConfig.discount}</p>
                        </div>
                        <div className="level-progress-text">
                            <span>Progresso</span>
                            <strong>{progressCurrent}/{targetVisits}</strong>
                        </div>
                    </div>

                    <div className="level-progress-bar">
                        <span
                            style={{
                                width: `${progressPercent}%`,
                                background: levelConfig.color,
                            }}
                        />
                    </div>

                    <div className="level-warning">
                        <Award size={18} />
                        <div>
                            <strong>Faltam apenas {Math.max(targetVisits - progressCurrent, 0)} usos para {getNextLevelLabel()}!</strong>
                            <p>Continue usando o app para desbloquear mais beneficios</p>
                        </div>
                    </div>

                    <p className="level-subtitle">Niveis disponiveis:</p>
                    <div className="level-list">
                        {(sortedLevels.length > 0 ? sortedLevels : [
                            { id: 'bronze', slug: 'bronze', minVisits: 10, title: 'Nível Bronze', discount: '3% a 5% de desconto', color: '#ea580c', sortOrder: 1 },
                            { id: 'silver', slug: 'silver', minVisits: 20, title: 'Nível Prata', discount: '5% a 7% de desconto', color: '#94a3b8', sortOrder: 2 },
                            { id: 'gold', slug: 'gold', minVisits: 30, title: 'Nível Ouro', discount: '7% a 10% + Ofertas exclusivas', color: '#eab308', sortOrder: 3 },
                        ] as Level[]).map((l) => (
                            <div key={l.id} className={`level-item ${currentLevel?.slug === l.slug ? 'active' : ''}`}>
                                <span className="dot" style={{ background: l.color }} />
                                <div>
                                    <strong>{l.title.replace('Nível ', '')}</strong>
                                    <small>{l.discount}</small>
                                    <small className="level-target">({l.minVisits} visitas)</small>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="level-note">Atencao: sem uso por 60 dias, voce volta ao nivel anterior</div>
                </section>

                <section className="card recent-card">
                    <div className="recent-header">
                        <h3>
                            <MapPin size={18} />
                            Locais Visitados Recentemente
                        </h3>
                        <button type="button" onClick={() => navigate('/benefits')}>
                            Ver todos <ChevronRight size={16} />
                        </button>
                    </div>

                    <div className="recent-list">
                        {history.length === 0 && (
                            <div className="recent-item empty">
                                <p>Sem visitas recentes.</p>
                            </div>
                        )}
                        {history.slice(0, 3).map((item) => {
                            const saved = getSavedValue(item);
                            const purchase = item.purchaseAmount ?? 0;
                            return (
                                <div key={item.id} className="recent-item">
                                    <div>
                                        <strong>{item.establishment?.name || 'Estabelecimento'}</strong>
                                        <span>
                                            {item.usedAt
                                                ? new Date(item.usedAt).toLocaleDateString('pt-BR', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                })
                                                : 'Uso recente'}
                                        </span>
                                    </div>
                                    <div className="recent-item-values">
                                        {purchase > 0 && (
                                            <p className="purchase-value">
                                                Compra: {purchase.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                            </p>
                                        )}
                                        <p className="saved-value">
                                            -{saved.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} economizado
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <section className="card menu-card">
                    {menuItems.map((item) => (
                        <button key={item.key} className="menu-item" onClick={item.action} type="button">
                            <span className={`menu-icon ${item.iconClass}`}>
                                <item.icon size={22} />
                            </span>
                            <span className="menu-label">{item.label}</span>
                            <ChevronRight size={20} className="menu-chevron" />
                        </button>
                    ))}
                </section>

                <button type="button" className="logout-btn" onClick={handleLogout}>
                    <ArrowRightLeft size={22} />
                    Sair
                </button>
            </div>
        </div>
    );
};

export default Profile;


